/**
 * 各能力的 React 绑定。
 *
 * 每个 hook 的返回值都区分「不能用」的**原因**，而不是一个 boolean——
 * "浏览器不支持"和"你得先加到主屏幕"和"你拒绝过"需要三种不同的界面文案，
 * 而返回 boolean 的 API 会让每个项目重新发明一遍这个区分，然后漏掉其中一种。
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { installPrompt } from '@pwaship/core/install';
import { explainBadgeSupport, explainNotificationSupport, explainPushSupport } from '@pwaship/core/capability';
import { subscribePush, unsubscribePush, currentSubscription } from '@pwaship/core/push-client';
import { createScreenWakeLock, wakeLockSupported } from '@pwaship/core/wake-lock';
import { createOnlineState } from '@pwaship/core/net';
import { usePwa } from './provider.js';
export function useInstall() {
    const { install, platform, telemetry } = usePwa();
    const canPromptNatively = useSyncExternalStore(installPrompt.subscribe, installPrompt.available, () => false);
    const [, force] = useState(0);
    return {
        standalone: install.standalone(),
        shouldPrompt: install.shouldPrompt(),
        canPromptNatively,
        browser: platform.browser,
        prompt: useCallback(async () => {
            telemetry.emit({ type: 'install.prompt.shown', source: 'system' });
            const r = await installPrompt.prompt();
            telemetry.emit({ type: 'install.prompt.result', outcome: r.ok ? r.value : 'unavailable' });
            if (r.ok && r.value === 'accepted')
                install.markDone();
            force((n) => n + 1);
            return r;
        }, [install, telemetry]),
        markDone: useCallback(() => {
            install.markDone();
            telemetry.emit({ type: 'install.marked-done', manual: true });
            force((n) => n + 1);
        }, [install, telemetry]),
    };
}
export function useNotificationPermission() {
    const { permission, capabilities, telemetry } = usePwa();
    const state = useSyncExternalStore(permission.subscribe, permission.state, () => 'unsupported');
    return {
        state,
        support: useMemo(() => explainNotificationSupport(capabilities), [capabilities]),
        primerShown: permission.primerShown(),
        markPrimerShown: useCallback(() => {
            permission.markPrimerShown();
            telemetry.emit({ type: 'permission.primer.shown' });
        }, [permission, telemetry]),
        request: useCallback(async () => {
            const r = await permission.request();
            telemetry.emit({
                type: 'permission.result',
                result: r.ok
                    ? 'granted'
                    : r.reason === 'permission-denied'
                        ? 'denied'
                        : r.reason === 'requires-user-gesture'
                            ? 'blocked-no-gesture'
                            : r.reason === 'permission-default'
                                ? 'default'
                                : 'unsupported',
            });
            return r;
        }, [permission, telemetry]),
    };
}
export function usePush(options = {}) {
    const { registration, capabilities, telemetry } = usePwa();
    const { state: permission } = useNotificationPermission();
    const [subscription, setSubscription] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const support = useMemo(() => explainPushSupport(capabilities), [capabilities]);
    // ⚠️ **回调放进 ref，绝不能进依赖数组。**
    // 正常用法就是内联箭头（`usePush({ vapidPublicKey, report: (s) => fetch(...) })`），
    // 它每次渲染都是新身份。让它进 subscribe 的依赖，就成了：
    //   新 report → 新 subscribe → 自动订阅 effect 重跑 → setBusy 触发渲染 → 新 report …
    // 结果是**不停地调 pushManager.subscribe() 并往上报接口 POST**，谁都不会立刻发现，
    // 只有服务端日志或者用户的流量会告诉你。
    const cb = useRef({ report: options.report, revoke: options.revoke });
    cb.current = { report: options.report, revoke: options.revoke };
    useEffect(() => {
        if (!registration)
            return;
        void currentSubscription(registration).then(setSubscription);
    }, [registration]);
    const subscribe = useCallback(async () => {
        if (!registration)
            return { ok: false, reason: 'no-service-worker' };
        if (!options.vapidPublicKey)
            return { ok: false, reason: 'failed', detail: '还没拿到服务器的 VAPID 公钥' };
        setBusy(true);
        try {
            const opts = {
                registration,
                vapidPublicKey: options.vapidPublicKey,
                // 从 ref 里读，保证 subscribe 的身份只跟 registration / 公钥走
                ...(cb.current.report ? { report: (sub) => cb.current.report(sub) } : {}),
            };
            const r = await subscribePush(opts);
            if (r.ok) {
                setSubscription(r.value);
                setError(null);
            }
            else {
                setError(r);
            }
            telemetry.emit({ type: 'push.subscribe', ok: r.ok, ...(r.ok ? {} : { reason: r.reason }) });
            return r;
        }
        finally {
            setBusy(false);
        }
    }, [registration, options.vapidPublicKey, telemetry]);
    const unsubscribe = useCallback(async () => {
        if (!registration)
            return { ok: false, reason: 'no-service-worker' };
        setBusy(true);
        try {
            const r = await unsubscribePush(registration, cb.current.revoke);
            if (r.ok)
                setSubscription(null);
            telemetry.emit({ type: 'push.unsubscribe', ok: r.ok });
            return r;
        }
        finally {
            setBusy(false);
        }
    }, [registration, telemetry]);
    // 已授权 + 有公钥 → 自动订阅。**每次启动都重订一次**，服务端的记录才跟得上端点变化，
    // 而且订阅自愈（VAPID 换过时退订重订）也在这条路径上。
    const auto = options.auto ?? true;
    useEffect(() => {
        if (!auto || permission !== 'granted' || !registration || !options.vapidPublicKey)
            return;
        void subscribe();
    }, [auto, permission, registration, options.vapidPublicKey, subscribe]);
    return { support, subscription, busy, error, subscribe, unsubscribe };
}
export function useUnread() {
    const { unread, capabilities, telemetry } = usePwa();
    const [total, setTotal] = useState(() => unread.current());
    return {
        total,
        support: useMemo(() => explainBadgeSupport(capabilities), [capabilities]),
        set: useCallback(async (next, options) => {
            const r = await unread.set(next, options);
            setTotal(unread.current());
            // ⚠️ 用真实结果，不要硬编码 ok: true。
            // iOS 上没通知权限时 setAppBadge 会拒绝、角标根本没设上，
            // 而这条遥测正是为了发现那件事而存在的——报成功等于把唯一的探针关掉了。
            telemetry.emit({ type: 'badge.set', count: next, ok: r.badge });
        }, [unread, telemetry]),
        reconcile: useCallback(async () => {
            await unread.reconcile();
            setTotal(unread.current());
        }, [unread]),
    };
}
export function useAppUpdate() {
    const { update, updateAvailable, telemetry } = usePwa();
    return {
        available: updateAvailable,
        apply: useCallback(async () => {
            telemetry.emit({ type: 'sw.update-applied' });
            await update?.applyUpdate();
        }, [update, telemetry]),
        check: useCallback(async () => update?.check(), [update]),
        version: useCallback(async () => (await update?.version())?.version ?? null, [update]),
    };
}
/* ─────────────────────────── 在线状态 ─────────────────────────── */
const onlineState = typeof window === 'undefined' ? null : createOnlineState();
export function useOnline() {
    return useSyncExternalStore((cb) => onlineState?.subscribe(() => cb()) ?? (() => { }), () => onlineState?.online() ?? true, () => true);
}
export function useWakeLock() {
    const { telemetry } = usePwa();
    const [active, setActive] = useState(false);
    const lock = useMemo(() => createScreenWakeLock({
        onChange: (isActive, reason) => {
            setActive(isActive);
            telemetry.emit({ type: 'wakelock', active: isActive, ...(reason ? { reason } : {}) });
        },
    }), [telemetry]);
    // 组件卸载时一定要释放：忘了释放就是"用户关了这个页面但屏幕还一直亮着"
    useEffect(() => () => void lock.release(), [lock]);
    return { supported: wakeLockSupported(), active, acquire: lock.acquire, release: lock.release };
}
/**
 * 订阅中性化路由。需要在 `PwaProvider` 的 config 里 `neutralHistory: true` 才生效。
 *
 * 没开的时候它退化成"读 location、navigate 无效"，**不抛错**——
 * 这样组件代码不用为两种模式写两套分支，而开关它是一个配置项而不是一次重构。
 */
export function useNeutralPath() {
    const { history } = usePwa();
    const subscribe = useCallback((cb) => history?.subscribe(() => cb()) ?? (() => { }), [history]);
    const get = useCallback(() => {
        if (history)
            return history.path();
        if (typeof window === 'undefined')
            return '/';
        return `${window.location.pathname}${window.location.search}${window.location.hash}`;
    }, [history]);
    const path = useSyncExternalStore(subscribe, get, () => '/');
    return {
        enabled: !!history,
        path,
        navigate: useCallback((to, options) => history?.navigate(to, options), [history]),
    };
}
/* ─────────────────────────── 切后台 ─────────────────────────── */
/**
 * 应用是不是正在后台/失焦。用来盖住应用切换器的快照（见 `<BackdropCover>`），
 * 也可以拿来暂停播放、停轮询、锁上应用锁。
 *
 * ⚠️ **三个事件都要听**：`visibilitychange` / `pagehide` / `blur`。
 * 不同平台真正派发的那一个不一致，少听一个就是"在某个平台上完全不生效"，
 * 而且这种失效不会报错——你只会在某天划开多任务时看见自己的聊天记录。
 *
 * ⚠️ 这一条**降低概率，不消除风险**：iOS 上系统截图的时机不由网页决定，
 * 完全可能在 `visibilitychange` 派发之前就截了。别把它当"已解决"。
 */
export function useBackgrounded() {
    const [backgrounded, setBackgrounded] = useState(() => typeof document !== 'undefined' && document.visibilityState === 'hidden');
    useEffect(() => {
        if (typeof document === 'undefined' || typeof window === 'undefined')
            return;
        const cover = () => setBackgrounded(true);
        const reveal = () => setBackgrounded(false);
        const onVisibility = () => (document.visibilityState === 'visible' ? reveal() : cover());
        const onBlur = () => {
            // 焦点跑进页面内部的 iframe 时也会触发 window blur——那不是切后台。
            // 不排掉的话，任何嵌了 iframe 的页面点一下就整个被盖住。
            if (document.activeElement?.tagName === 'IFRAME')
                return;
            cover();
        };
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('pagehide', cover);
        window.addEventListener('blur', onBlur);
        window.addEventListener('focus', reveal);
        // bfcache 恢复走的是 pageshow，不是 focus
        window.addEventListener('pageshow', reveal);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pagehide', cover);
            window.removeEventListener('blur', onBlur);
            window.removeEventListener('focus', reveal);
            window.removeEventListener('pageshow', reveal);
        };
    }, []);
    return backgrounded;
}
/* ─────────────────────────── 响应式 ─────────────────────────── */
/**
 * ⚠️ 响应式判据用**宽度**，不要用 UA。
 * iPad 横屏、分屏、把窗口拖窄——这些 UA 全都判不出来。
 */
export function useMediaQuery(query) {
    const subscribe = useCallback((cb) => {
        if (typeof window === 'undefined' || !window.matchMedia)
            return () => { };
        const mql = window.matchMedia(query);
        mql.addEventListener('change', cb);
        return () => mql.removeEventListener('change', cb);
    }, [query]);
    const get = useCallback(() => {
        if (typeof window === 'undefined' || !window.matchMedia)
            return false;
        return window.matchMedia(query).matches;
    }, [query]);
    return useSyncExternalStore(subscribe, get, () => false);
}
export function useIsDesktop(minWidth = 1024) {
    return useMediaQuery(`(min-width: ${minWidth}px)`);
}
/**
 * 有没有精确指针（鼠标 / 触控笔）。
 *
 * ⚠️ **这与 `useIsDesktop()` 是两回事，别混用。**
 * - 「宽不宽」决定**布局**（侧栏还是底部标签栏）——iPad 横屏、桌面把窗口拖窄，
 *   这两种情况下布局都该跟着宽度走；
 * - 「指针精不精确」决定**交互**（要不要 hover、要不要右键菜单、下拉刷新有没有意义）——
 *   一台带触摸屏的 Windows 笔记本既宽又是触摸，两个判据的答案不一样。
 *
 * 用错的表现：在触屏笔记本上 hover 高亮"粘住"点完不消失，或者鼠标用户拖不出下拉刷新
 * 却也看不到刷新按钮。
 */
export function usePointerFine() {
    return useMediaQuery('(pointer: fine)');
}
/** 有没有粗指针（手指）。触屏笔记本上它和 `usePointerFine()` 可以同时为真。 */
export function usePointerCoarse() {
    return useMediaQuery('(pointer: coarse)');
}
/** 能不能 hover。触屏上 hover 会"粘住"，所以任何 hover 效果都要先问这一句。 */
export function useHasHover() {
    return useMediaQuery('(hover: hover)');
}
/**
 * Window Controls Overlay：已安装的桌面 PWA 可以把系统标题栏让给应用自己画。
 *
 * 这是"看起来像桌面应用而不是一个网页"最有效的一招——顶上那条灰色标题栏没了，
 * 应用自己的导航直接顶到窗口边缘。
 *
 * 要用它，manifest 里必须有 `"display_override": ["window-controls-overlay", ...]`，
 * 而且**只在已安装的桌面窗口里生效**，浏览器标签页里永远是 false。
 *
 * ⚠️ 画在那条区域里的东西必须给窗口控件让位：`env(titlebar-area-x/width)` 才是
 * 你能用的横向范围，直接铺满会被最小化/关闭按钮盖住（而且在 macOS 和 Windows 上
 * 那几个按钮在**不同的一侧**）。CSS 里用 `--ps-titlebar-*` 那组变量。
 */
export function useTitlebarArea() {
    const [area, setArea] = useState({ overlayVisible: false, height: 0 });
    useEffect(() => {
        if (typeof navigator === 'undefined')
            return;
        const wco = navigator
            .windowControlsOverlay;
        if (!wco)
            return;
        const read = () => {
            const rect = wco.getTitlebarAreaRect?.();
            setArea({ overlayVisible: !!wco.visible, height: wco.visible ? Math.round(rect?.height ?? 0) : 0 });
        };
        read();
        wco.addEventListener?.('geometrychange', read);
        return () => wco.removeEventListener?.('geometrychange', read);
    }, []);
    return area;
}
//# sourceMappingURL=hooks.js.map